import { action } from '@ember/object';
import Route from '@ember/routing/route';
import type PublicTransition from '@ember/routing/transition';
import { service } from '@ember/service';

import type { Service } from 'webapp-types';
import { PopupEvent, type BillingFeature } from 'webapp/enums/enums';

import type { CustomMetric } from 'webapp/models-custom';
import type Popup from 'webapp/models-custom/popup/base';

// A custom metric editor opened by its URL over the Metrics page; closing it returns to /metrics.
export default abstract class MetricsEditBaseRoute<ModelType extends CustomMetric> extends Route {
	@service('account') declare accountService: Service.Account;
	@service('navigation') declare navigationService: Service.Navigation;
	@service('infolayer') declare infolayerService: Service.Infolayer;
	@service('billing') declare billingService: Service.Billing;
	@service('metric-registry') declare metricRegistryService: Service.MetricRegistry;

	// Leaving closes the editor, and its close must not redirect that transition to /metrics.
	private isLeaving = false;

	protected abstract readonly notFoundText: string;
	protected abstract readonly billingFeature: BillingFeature | null;

	protected abstract openEditPopup(model: ModelType): Popup | null | undefined;

	override afterModel(model: ModelType | null): void {
		this.isLeaving = false;

		if (this.accountService.isDemoAccount) {
			return this.returnToRoot();
		}

		if (!model) {
			return this.returnToRoot(true);
		}

		// Checked here, not by openEditPopup, because the limitation popup it opens would close with the transition.
		const feature = this.billingFeature;
		if (feature && !this.billingService.checkIfCan(feature)) {
			void this.navigationService.replaceRoute('metrics').then(() => {
				this.billingService.handleLimitations(feature);
			});
			return;
		}

		const popup = this.openEditPopup(model);
		if (!popup) {
			return this.returnToRoot();
		}

		this.metricRegistryService.watchEditor(popup, model);

		popup.on(PopupEvent.CLOSING, this, () => {
			if (!this.isLeaving) {
				this.returnToRoot();
			}
		});
	}

	// Fires before the router's routeWillChange, where the navigation service closes all popups.
	@action
	override willTransition(_transition: PublicTransition): void {
		this.isLeaving = true;
	}

	private returnToRoot(withWarning = false): void {
		if (withWarning) {
			this.infolayerService.error(this.notFoundText);
		}
		void this.navigationService.replaceRoute('metrics');
	}
}
